import React, { useEffect, useMemo, useRef, useState } from "react";
import Global from "../../Services/Common/Global";
import Commons from "../../Services/Common/Common";
import swal from "../SweetAlert2";

function SmsVerification({
  phone,
  originalPhone = "",
  translations = {
    sendVerificationCode: "인증번호 전송",
    verifyCode: "인증번호 확인",
    resendCode: "재전송",
    codeSent: "인증번호가 전송되었습니다",
    codeVerified: "인증이 완료되었습니다",
    codeInvalid: "인증번호가 올바르지 않습니다",
    phoneNotChanged: "전화번호를 변경한 후 인증번호를 전송해주세요",
    phoneFormatInvalid: "올바른 전화번호 형식이 아닙니다",
  },
  onVerified,
  isVerified = false,
  durationSeconds = 180,
}) {
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const isValidPhoneNumber = useMemo(() => {
    if (!phone) return false;
    const digits = (phone || "").replace(/[^\d]/g, "");
    return digits.length >= 10 && digits.length <= 11;
  }, [phone]);

  const startCountdown = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setCountdown(durationSeconds);
    timerRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const sendCode = async () => {
    if (!phone || !isValidPhoneNumber) return;
    if (phone === originalPhone) {
      await swal.info(translations.phoneNotChanged);
      return;
    }
    try {
      const authCode = Math.floor(100000 + Math.random() * 900000).toString();
      const params = {
        user_id: process.env.REACT_APP_SMS_API_USER_ID,
        key: process.env.REACT_APP_SMS_API_KEY,
        msg: `SafeWay 인증번호\n\n인증번호: ${authCode}\n\n3분 이내에 입력해주세요.`,
        receiver: phone,
        destination: phone,
        sender: "",
        rdate: "",
        rtime: "",
        testmode_yn: "N",
        title: "SafeWay 인증번호",
        image_path: "",
        companycode: "VBSOMNG-1",
      };

      const response = await Commons.Axios(
        "post",
        process.env.REACT_APP_SMS_API_URL + Global.urls.Sms.notAuthSendMessage,
        {},
        params
      );
      if (response.data && response.data.type !== "Error") {
        setGeneratedCode(authCode);
        setVerificationCode("");
        setIsCodeSent(true);
        startCountdown();
        await swal.success(translations.codeSent);
      } else {
        const errorMessage = response.data?.message || "인증번호 전송에 실패했습니다.";
        await swal.error(errorMessage);
      }
    } catch (e) {
      console.log("SMS send error", e);
      await swal.error("인증번호 전송 중 오류가 발생했습니다.");
    }
  };

  const verify = async () => {
    if (verificationCode === generatedCode && verificationCode.length === 6) {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      setCountdown(0);
      onVerified && onVerified();
      await swal.success(translations.codeVerified);
    } else {
      await swal.error(translations.codeInvalid);
    }
  };

  if (isVerified) {
    return (
      <div className='sms-verification-container'>
        <span className='countdown' style={{ color: "#28a745" }}>{translations.codeVerified}</span>
      </div>
    );
  }

  return (
    <div className='sms-verification-container'>
      {!isCodeSent ? (
        <button
          onClick={sendCode}
          className='sms-btn send-btn'
          disabled={!phone || !isValidPhoneNumber || phone === originalPhone}
          title={!phone || !isValidPhoneNumber ? translations.phoneFormatInvalid : phone === originalPhone ? translations.phoneNotChanged : translations.sendVerificationCode}
        >
          {translations.sendVerificationCode}
        </button>
      ) : (
        <div className='verification-input-container'>
          <input
            type='tel'
            inputMode='numeric'
            pattern='[0-9]*'
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => {
              const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"]; 
              if (!/[0-9]/.test(e.key) && !allowed.includes(e.key)) {
                e.preventDefault();
              }
            }}
            className='verification-input'
            placeholder='인증번호 6자리'
            maxLength={6}
          />
          {countdown > 0 && (
            <span className='countdown'>
              {Math.floor(countdown / 60)}:{(countdown % 60).toString().padStart(2, "0")}
            </span>
          )}
          <button onClick={verify} className='sms-btn verify-btn' disabled={verificationCode.length !== 6}>
            {translations.verifyCode}
          </button>
          {countdown === 0 && isCodeSent && (
            <button onClick={sendCode} className='sms-btn resend-btn'>
              {translations.resendCode}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default React.memo(SmsVerification);


